import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import { useReceipt, useReceiptStore, useCustomer } from "hooks";
import { Receipt, ReceiptItem, ReceiptQuery } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "utils";
import { Plus } from "lucide-react";
import { sanitizeReceiptItems } from "utils/helpers/sanitizers";
import { parseNumberSafe } from "utils/helpers/accounting";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const parseNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }
    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: ReceiptItem[]): ReceiptItem[] => {
  const sanitized = sanitizeReceiptItems(items);
  return sanitized
    .map((item) => {
      const description =
        (item as any).description !== undefined &&
        (item as any).description !== null
          ? String((item as any).description).trim()
          : undefined;
      const reason =
        item.reason !== undefined && item.reason !== null
          ? String(item.reason).trim()
          : description;
      const vatAmount =
        (item as any).vat_amount !== undefined
          ? parseNumber((item as any).vat_amount)
          : undefined;
      const pitAmount =
        (item as any).pit_amount !== undefined
          ? parseNumber((item as any).pit_amount)
          : undefined;
      const netAmount =
        (item as any).net_amount !== undefined
          ? parseNumber((item as any).net_amount)
          : undefined;

      return {
        ...((item as any).id ? { id: (item as any).id } : {}),
        ...item,
        ...(reason ? { reason } : {}),
        ...(description ? { description } : {}),
        ...(vatAmount !== undefined ? { vat_amount: vatAmount } : {}),
        ...(pitAmount !== undefined ? { pit_amount: pitAmount } : {}),
        ...(netAmount !== undefined ? { net_amount: netAmount } : {}),
      };
    })
    .filter(
      (item) =>
        parseNumberSafe(item.amount ?? 0) > 0 ||
        Boolean(item.partner_id) ||
        Boolean(item.reason) ||
        Boolean((item as any).description) ||
        item.vat_rate !== undefined ||
        item.pit_rate !== undefined ||
        (item as any).vat_amount ||
        (item as any).pit_amount ||
        (item as any).net_amount,
    );
};

const parseItems = (value: unknown): ReceiptItem[] => {
  if (Array.isArray(value)) {
    return sanitizeItems(value as ReceiptItem[]);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return sanitizeItems(parsed as ReceiptItem[]);
      }
    } catch {
      // fall back to empty when parsing fails
    }
  }

  return [];
};

const withItemFields = (receipt: Receipt): Receipt => {
  return {
    ...receipt,
    attachments: normalizeAttachments(receipt.attachments).join(", "),
    items: sanitizeItems(receipt.items),
  };
};

const Receipts: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<ReceiptQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<Receipt | null>(null);
  const [selectedItems, setSelectedItems] = useState<ReceiptItem[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const { rows, setRows, removeRow, isLoading, setLoading } = useReceiptStore();

  const { fetchList, deleteReceipt } = useReceipt();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: receiptData, isFetching } = fetchList(queryParams, true);

  useEffect(() => {
    if (receiptData?.data) {
      const mapped =
        (receiptData.data as Receipt[])?.map((item) => withItemFields(item)) ??
        [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          mapped[0].receipt_code ??
            mapped[0].object_name ??
            `Phiếu ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [receiptData?.data, setRows]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deleteReceipt.isPending));
  }, [isFetching, deleteReceipt.isPending, setLoading]);

  const columns = useMemo<ColDef<Receipt>[]>(() => {
    return [
      {
        headerName: "Mã phiếu thu",
        field: "receipt_code",
        minWidth: 160,
      },
      {
        headerName: "Ngày phiếu thu",
        field: "receipt_date",
        minWidth: 150,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Đơn vị/Đối tượng",
        field: "object_name",
        minWidth: 200,
      },
      {
        headerName: "Người nộp",
        field: "payer_name",
        minWidth: 180,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
      },
      {
        headerName: "Nội dung",
        field: "description",
        flex: 1,
        minWidth: 220,
      },
      {
        headerName: "Đính kèm",
        field: "attachments",
        minWidth: 180,
        valueFormatter: (params) => {
          if (Array.isArray(params.value)) return params.value.join(", ");
          return params.value || "";
        },
      },
      {
        headerName: "Số CT gốc",
        field: "original_voucher_no",
        minWidth: 140,
      },
      {
        headerName: "Hóa đơn xuất (ID)",
        field: "export_invoice_id",
        minWidth: 150,
        filter: "agNumberColumnFilter",
        valueParser: (value) => {
          const parsed = Number(value);
          return Number.isNaN(parsed) ? undefined : parsed;
        },
      },
      {
        headerName: "Tổng tiền",
        field: "items",
        flex: 1,
        minWidth: 260,
        valueFormatter: (params) => {
          if (Array.isArray(params.value)) {
            if (!params.value.length) return "";
            const total = params.value.reduce((sum, item) => {
              const candidate = (item as any).net_amount ?? item.amount ?? 0;
              return sum + parseNumber(candidate);
            }, 0);
            return `${total.toLocaleString("vi-VN")} ₫`;
          }
          if (typeof params.value === "string") return params.value;
          return "";
        },
        valueSetter: (params) => {
          const parsed = parseItems(params.newValue);
          (params.data as Receipt).items = parsed;
          return true;
        },
      },
    ];
  }, []);

  const handleDeleteRow = useCallback((row: Receipt) => {
    setConfirmRow(row);
  }, []);

  const handleEditRow = useCallback(
    (params: any) => {
      const id = params?.data?.id;
      if (id) {
        navigate(`${ROUTES.ACCOUNTING.CASH_RECEIPT_DETAIL}/${id}`);
      }
    },
    [navigate],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReceipt.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu thu không thành công");
      },
    });
  }, [confirmRow, deleteReceipt, removeRow]);

  const handleRowClick = useCallback((event: any) => {
    const items = sanitizeItems(event?.data?.items);
    setSelectedItems(items);
    setSelectedLabel(
      event?.data?.receipt_code ??
        event?.data?.object_name ??
        `Phiếu ${event?.data?.id ?? ""}`,
    );
  }, []);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const customerLabelById = useMemo(() => {
    const map = new Map<number | string, string>();
    (customersData?.data as any[])?.forEach((c) => {
      const label = `${c.code}${c.name ? ` - ${c.name}` : ""}`;
      map.set(c.id, label);
    });
    return map;
  }, [customersData]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Phiếu thu</h1>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            {/* <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu thu
            </h2> */}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.CASH_RECEIPT_CREATE)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4 text-green-700" />
              Thêm phiếu thu
            </button>
          </div>
        </div>
        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết dòng thu {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
            {/* <p className="text-xs text-gray-500">
              Chỉ xem, không chỉnh sửa. Chọn phiếu thu để xem các khoản thu.
            </p> */}
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Khách hàng/NCC
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">VAT</th>
                  <th className="px-3 py-2 text-right font-semibold">PIT</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Thực nhận
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Lý do</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.partner_id
                          ? (customerLabelById.get(item.partner_id) ??
                            String(item.partner_id))
                          : ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {Number(item.amount ?? 0).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item?.vat_amount !== undefined
                          ? Number(item?.vat_amount ?? 0).toLocaleString(
                              "vi-VN",
                            )
                          : item?.vat_rate !== undefined
                            ? `${item.vat_rate}%`
                            : ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item?.pit_amount !== undefined
                          ? Number(item?.pit_amount ?? 0).toLocaleString(
                              "vi-VN",
                            )
                          : item?.pit_rate !== undefined
                            ? `${item.pit_rate}%`
                            : ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {(item as any).net_amount !== undefined
                          ? Number(
                              (item as any).net_amount ?? 0,
                            ).toLocaleString("vi-VN")
                          : Number(item.amount ?? 0).toLocaleString("vi-VN")}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.reason ?? (item as any).description ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn phiếu thu để xem chi tiết dòng thu.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={560}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={receiptData?.totalPage}
          onDelete={handleDeleteRow}
          onEdit={handleEditRow}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu thu, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu thu?"
        description={`Bạn chắc chắn muốn xóa phiếu thu "${confirmRow?.receipt_code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReceipt.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default Receipts;
